/**
 * Auth context + useAuth hook for the dashboard SPA.
 *
 * Behavior:
 *  - VITE_AUTH_ENABLED=false → AuthProvider provides a permanent "unauthenticated
 *    bypass" state. useAuth().isAuthenticated is `true` (so route guards pass)
 *    and idToken is null. Matches Terraform's dev_auth_bypass.
 *  - VITE_AUTH_ENABLED=true → On mount, reads tokens from sessionStorage. If
 *    none, login() redirects to Cognito Hosted UI. Callback page (AuthCallback)
 *    finishes the exchange and saves the token. logout() clears + redirects
 *    to Cognito's /logout.
 */

import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react'

import { authConfig, assertAuthConfigValid } from './config'
import { generateCodeChallenge, generateCodeVerifier, generateState } from './pkce'
import { buildLoginUrl, buildLogoutUrl, decodeIdToken } from './oauth'
import { clearTokens, readTokens, stashPkce, writeTokens, StoredTokens } from './storage'

export interface AuthUser {
  sub: string
  email: string | null
  username: string | null
}

export interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  user: AuthUser | null
  idToken: string | null
  login: (returnTo?: string) => Promise<void>
  logout: () => void
  /** Called by AuthCallback after a successful token exchange. */
  acceptTokens: (tokens: StoredTokens) => void
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  // Bypass mode: short-circuit with a synthetic always-authenticated state.
  if (!authConfig.enabled) {
    return <AuthContext.Provider value={bypassState()}>{children}</AuthContext.Provider>
  }

  assertAuthConfigValid()
  return <RealAuthProvider>{children}</RealAuthProvider>
}

function RealAuthProvider({ children }: { children: ReactNode }) {
  const [tokens, setTokens] = useState<StoredTokens | null>(() => readTokens())
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    // Tokens may have expired between mount and now; readTokens validates exp.
    setTokens(readTokens())
  }, [])

  const login = async (returnTo: string = window.location.pathname + window.location.search) => {
    setIsLoading(true)
    const verifier = generateCodeVerifier()
    const challenge = await generateCodeChallenge(verifier)
    const state = generateState()
    stashPkce(verifier, state, returnTo)
    window.location.assign(buildLoginUrl({ codeChallenge: challenge, state }))
  }

  const logout = () => {
    clearTokens()
    setTokens(null)
    window.location.assign(buildLogoutUrl())
  }

  const acceptTokens = (next: StoredTokens) => {
    writeTokens(next)
    setTokens(next)
  }

  const user = useMemo<AuthUser | null>(() => {
    if (!tokens) return null
    try {
      const claims = decodeIdToken(tokens.idToken)
      return {
        sub: claims.sub,
        email: (claims.email as string) ?? null,
        username:
          (claims['cognito:username'] as string) ??
          (claims.username as string) ??
          null,
      }
    } catch {
      return null
    }
  }, [tokens])

  const value: AuthState = {
    isAuthenticated: tokens !== null,
    isLoading,
    user,
    idToken: tokens?.idToken ?? null,
    login,
    logout,
    acceptTokens,
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

function bypassState(): AuthState {
  return {
    isAuthenticated: true,
    isLoading: false,
    user: null,
    idToken: null,
    login: async () => {},
    logout: () => {},
    acceptTokens: () => {},
  }
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
